import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import './Reviews.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

const EMPTY = { parentName: '', childFirstName: '', childAge: '', rating: 5, quote: '', company: '' };

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/reviews`);
        const data = await res.json();
        if (!cancelled) setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      } catch (_) {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: null })); };

  const validate = () => {
    const e = {};
    if (!form.parentName.trim()) e.parentName = 'Required';
    if (!form.quote.trim()) e.quote = 'Please share a few words';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await fetch(`${API_BASE}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch (_) { /* handled gracefully — still show the thank-you */ }
    setSubmitting(false);
    setSubmitted(true);
    setForm(EMPTY);
  };

  const childLabel = (r) => r.childFirstName
    ? `Parent of ${r.childFirstName}${r.childAge ? `, age ${r.childAge}` : ''}`
    : '';

  return (
    <>
      <Seo
        title="Parent Reviews"
        description="Read what families say about celebrating their child's early achievements with Stratos Standard, and share your own story."
        path="/reviews"
      />
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>Families Love Us</p>
          <h1 className="page-hero-title">Parent Reviews</h1>
          <p className="page-hero-sub">Hear from the families who've celebrated their child's achievements with us, and share your own story.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container reviews-layout">
          {/* Existing approved reviews */}
          <div className="reviews-list">
            <h2 className="reviews-heading">What Parents Are Saying</h2>
            {loading ? (
              <p className="reviews-muted">Loading reviews…</p>
            ) : reviews.length === 0 ? (
              <p className="reviews-muted">No reviews yet. Be the first to share your experience!</p>
            ) : (
              <div className="reviews-grid">
                {reviews.map((r) => (
                  <div key={r.reviewId} className="review-card">
                    <div className="review-stars">{'★'.repeat(Math.max(1, Math.min(5, r.rating || 5)))}</div>
                    <blockquote className="review-quote">"{r.quote}"</blockquote>
                    <div className="review-author">
                      <span className="review-avatar">{(r.parentName || '?')[0]}</span>
                      <span>
                        <span className="review-name">{r.parentName}</span>
                        {childLabel(r) && <span className="review-child">{childLabel(r)}</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submission form */}
          <div className="review-form-wrap">
            {submitted ? (
              <div className="review-success">
                <div className="review-success-icon">⭐</div>
                <h3>Thank You!</h3>
                <p>Your review has been received and will appear here once our team approves it. We appreciate you sharing your story.</p>
                <button className="btn btn-aero" onClick={() => setSubmitted(false)}>Write another</button>
              </div>
            ) : (
              <form className="review-form" onSubmit={handleSubmit} noValidate>
                <h3>Share Your Experience</h3>

                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input
                    type="text"
                    className={`form-input ${errors.parentName ? 'input-error' : ''}`}
                    value={form.parentName}
                    onChange={e => set('parentName', e.target.value)}
                    placeholder="Jane S."
                    maxLength={80}
                  />
                  {errors.parentName && <span className="field-error">{errors.parentName}</span>}
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Child's First Name (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={form.childFirstName}
                      onChange={e => set('childFirstName', e.target.value)}
                      placeholder="Arjun"
                      maxLength={40}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Child's Age (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={form.childAge}
                      onChange={e => set('childAge', e.target.value)}
                      placeholder="2"
                      maxLength={24}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Rating</label>
                  <div className="star-input" role="radiogroup" aria-label="Star rating">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        type="button"
                        key={n}
                        className={`star-btn ${n <= form.rating ? 'on' : ''}`}
                        onClick={() => set('rating', n)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        aria-pressed={n === form.rating}
                      >★</button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Your Review *</label>
                  <textarea
                    className={`form-textarea ${errors.quote ? 'input-error' : ''}`}
                    value={form.quote}
                    onChange={e => set('quote', e.target.value)}
                    placeholder="Tell us about your child's session…"
                    rows={5}
                    maxLength={1500}
                  />
                  {errors.quote && <span className="field-error">{errors.quote}</span>}
                </div>

                {/* Honeypot — hidden from real users; bots that fill it are silently dropped. */}
                <div className="hp-field" aria-hidden="true">
                  <label>Company<input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={e => set('company', e.target.value)} /></label>
                </div>

                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Review'}
                </button>
                <p className="review-disclaimer">Reviews are published after a quick check by our team.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
